import { useState, lazy, Suspense } from 'react'
import { format } from 'date-fns'
import { ar } from 'date-fns/locale'
import { Card, CardContent } from '@/components/ui/card'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import { MapPin, Package, Heart, Share2, CalendarDays } from 'lucide-react'
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from '@/components/ui/hover-card'
import { useStoreFollowersPreview } from '@/hooks/storePageHooks'
import FollowButton from '../FollowButton'
import { Plus, Store, BarChart3 } from 'lucide-react'
import { ImpersonateActionButton } from '../ImpersonateActionButton'
import { StoreLogo } from '@/components/store/StoreLogo'
import { StoreCover } from '@/components/store/StoreCover'

const FollowersListModal = lazy(() => import('./FollowersListModal'))
const ShareDialog = lazy(() => import('@/components/ShareDialog'))
const StoreLightbox = lazy(() => import('./StoreLightbox'))

const StoreProfileCard = ({ store }) => {
  const [followersModalOpen, setFollowersModalOpen] = useState(false)
  const {
    data: followersPreview = [],
    refetch,
    isFetching,
  } = useStoreFollowersPreview(store.slug)
  const followersCount = store.followers_count || 0
  const [shareModalOpen, setShareModalOpen] = useState(false)
  const storeUrl =
    typeof window !== 'undefined'
      ? `${window.location.origin}/store/${store.slug}`
      : ''

  const [lightboxVisible, setLightboxVisible] = useState(false)
  const [initialPhotoIndex, setInitialPhotoIndex] = useState(0)

  const getBestImageUrl = (urls) => {
    if (!urls) return ''
    return urls.big || urls.url || urls.medium || urls.small || ''
  }

  const coverUrl = getBestImageUrl(store.cover_urls)
  const logoUrl = getBestImageUrl(store.logo_urls)
  const images = []
  if (coverUrl) images.push({ src: coverUrl, alt: `${store.name} cover` })
  if (logoUrl) images.push({ src: logoUrl, alt: `${store.name} logo` })

  const handleCoverClick = () => {
    setInitialPhotoIndex(0)
    setLightboxVisible(true)
  }

  const handleLogoClick = () => {
    const logoIndex = coverUrl ? 1 : 0
    setInitialPhotoIndex(logoIndex)
    setLightboxVisible(true)
  }

  const handleHoverChange = (open) => {
    if (open && followersCount > 0 && followersPreview.length === 0) {
      refetch()
    }
  }

  const joinDate = store.created_at
    ? format(new Date(store.created_at), 'dd MMMM yyyy', { locale: ar })
    : null

  return (
    <>
      <Card className="overflow-hidden border border-border/50 shadow-sm">
        {/* Cover image - clickable */}
        <div
          className="relative h-48 sm:h-56 md:h-64 rounded-t-lg overflow-hidden border-b border-border/20 shadow-sm cursor-pointer"
          onClick={handleCoverClick}
        >
          {store.cover_urls && (
            <StoreCover
              urls={store.cover_urls}
              size="original"
              className="w-full h-full object-cover"
            />
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent pointer-events-none" />
        </div>

        <CardContent className="pt-0">
          {/* Overlapped section (avatar + buttons) */}
          <div className="flex flex-col items-center sm:flex-row sm:items-start gap-4 -mt-12 sm:-mt-16">
            <div
              className="h-38 w-38 rounded-full border-4 border-background shadow-lg overflow-hidden bg-muted flex-shrink-0 relative z-10 -mt-6 sm:-mt-10 cursor-pointer ring-2 ring-brand/50"
              onClick={handleLogoClick}
            >
              <StoreLogo store={store} size="medium" className="w-full h-full object-cover" />
            </div>

            <div className="flex-1 flex justify-center sm:justify-end z-10">
              <div className="flex flex-wrap justify-center gap-2">
                <Button
                  onClick={() => setShareModalOpen(true)}
                  variant="outline"
                  size="icon"
                  className="shrink-0"
                >
                  <Share2 className="h-4 w-4" />
                </Button>

                {store.is_owned ? (
                  <div className="flex flex-wrap justify-center gap-2">
                    <ImpersonateActionButton
                      store={store}
                      to="/dashboard/products/create"
                      variant="default"
                      size="sm"
                    >
                      <Plus className="w-4 h-4 ml-1" />
                      <span className="hidden sm:inline">إضافة منتج</span>
                      <span className="sm:hidden">إضافة</span>
                    </ImpersonateActionButton>

                    <ImpersonateActionButton
                      store={store}
                      to="/dashboard/infos"
                      variant="outline"
                      size="sm"
                    >
                      <Store className="w-4 h-4 ml-1" />
                      <span className="hidden sm:inline">تعديل المتجر</span>
                      <span className="sm:hidden">تعديل</span>
                    </ImpersonateActionButton>

                    <ImpersonateActionButton
                      store={store}
                      to="/dashboard/statistics"
                      variant="outline"
                      size="sm"
                    >
                      <BarChart3 className="w-4 h-4 ml-1" />
                      <span className="hidden sm:inline">الإحصائيات</span>
                      <span className="sm:hidden">إحصاءات</span>
                    </ImpersonateActionButton>
                  </div>
                ) : (
                  <FollowButton
                    storeSlug={store.slug}
                    initialFollowed={store.is_followed}
                  />
                )}
              </div>
            </div>
          </div>

          {/* Title and store type */}
          <div className="flex flex-col sm:flex-row justify-between items-center sm:items-start gap-4 mt-4">
            <div>
              <div className="mt-6 text-center sm:text-right">
                <h1 className="text-2xl sm:text-3xl font-bold">{store.name}</h1>
                <p className="text-base text-muted-foreground">{store.store_type?.name}</p>
              </div>
              {store.description && (
                <p className="mt-4 text-sm text-muted-foreground leading-relaxed whitespace-pre-line max-sm:text-center">
                  {store.description}
                </p>
              )}
            </div>

            {/* NEW: Meta info – outline buttons, vertical stack, light & polished */}
            <div className="mt-6 flex flex-col gap-2">
              <Button variant="outline" size="sm" className="gap-1.5 justify-center w-full sm:w-auto">
                <MapPin className="h-3.5 w-3.5" />
                <span>{store.city?.name} • {store.neighborhood?.name}</span>
              </Button>
              <Button variant="outline" size="sm" className="gap-1.5 justify-center w-full sm:w-auto">
                <Package className="h-3.5 w-3.5" />
                <span>{store.products_count || 0} منتج</span>
              </Button>
              {joinDate && (
                <Button variant="outline" size="sm" className="gap-1.5 justify-center w-full sm:w-auto">
                  <CalendarDays className="h-3.5 w-3.5" />
                  <span>انضم {joinDate}</span>
                </Button>
              )}
              <HoverCard openDelay={200} onOpenChange={handleHoverChange}>
                <HoverCardTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    className="gap-1.5 justify-center w-full sm:w-auto cursor-pointer"
                    onClick={() => {
                      if (followersCount > 0) setFollowersModalOpen(true)
                    }}
                  >
                    <Heart className="h-3.5 w-3.5" />
                    <span>{followersCount} متابع</span>
                  </Button>
                </HoverCardTrigger>
                <HoverCardContent className="w-64" align="start">
                  <div className="space-y-2">
                    <h4 className="text-sm font-semibold">المتابعون</h4>
                    {followersCount === 0 ? (
                      <p className="text-xs text-muted-foreground">لا يوجد متابعون بعد</p>
                    ) : isFetching ? (
                      <div className="space-y-2">
                        {[...Array(3)].map((_, i) => (
                          <div key={i} className="flex items-center gap-2">
                            <div className="h-6 w-6 rounded-full bg-muted animate-pulse" />
                            <div className="h-3 w-20 bg-muted rounded animate-pulse" />
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {followersPreview.map((user) => (
                          <div key={user.id} className="flex items-center gap-2">
                            <Avatar className="h-6 w-6">
                              <AvatarImage src={user.avatar} />
                              <AvatarFallback>{user.name?.charAt(0)}</AvatarFallback>
                            </Avatar>
                            <span className="text-xs">{user.name}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </HoverCardContent>
              </HoverCard>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Lazy loaded lightbox */}
      {images.length > 0 && lightboxVisible && (
        <Suspense fallback={null}>
          <StoreLightbox
            images={images}
            visible={lightboxVisible}
            onClose={() => setLightboxVisible(false)}
            index={initialPhotoIndex}
            onIndexChange={setInitialPhotoIndex}
          />
        </Suspense>
      )}

      {/* Lazy loaded modals */}
      {followersModalOpen && (
        <Suspense fallback={null}>
          <FollowersListModal
            open={followersModalOpen}
            onOpenChange={setFollowersModalOpen}
            storeSlug={store.slug}
            followersCount={followersCount}
          />
        </Suspense>
      )}

      {shareModalOpen && (
        <Suspense fallback={null}>
          <ShareDialog
            open={shareModalOpen}
            onOpenChange={setShareModalOpen}
            url={storeUrl}
            title={store.name}
            type="store"
          />
        </Suspense>
      )}
    </>
  )
}

export default StoreProfileCard